/**
 * The single IndexedDB database: token, cached library, and sync bookkeeping.
 *
 * One database for everything so signing out is one wipe, and so a schema
 * migration is one versioned upgrade rather than several that can drift apart.
 */
export const DB_NAME = "tv-tracker";
export const DB_VERSION = 4;

export const META_STORE = "meta";
export const SHOWS_STORE = "shows";
export const OUTBOX_STORE = "outbox";
export const EPISODES_STORE = "episodes";
export const DETAILS_STORE = "details";

/** Superseded by DETAILS_STORE, which keeps the whole `/tv/{id}` record. */
const AIRSTATUS_STORE = "airstatus";

let cached: Promise<IDBDatabase> | null = null;

function upgrade(db: IDBDatabase): void {
  if (!db.objectStoreNames.contains(META_STORE)) db.createObjectStore(META_STORE);
  if (!db.objectStoreNames.contains(SHOWS_STORE)) {
    const shows = db.createObjectStore(SHOWS_STORE, { keyPath: "simklId" });
    shows.createIndex("status", "status", { unique: false });
  }
  if (!db.objectStoreNames.contains(OUTBOX_STORE)) {
    db.createObjectStore(OUTBOX_STORE, { keyPath: "id" });
  }
  if (!db.objectStoreNames.contains(EPISODES_STORE)) {
    db.createObjectStore(EPISODES_STORE, { keyPath: "simklId" });
  }
  if (!db.objectStoreNames.contains(DETAILS_STORE)) {
    db.createObjectStore(DETAILS_STORE, { keyPath: "simklId" });
  }
  // The airing flag it held is one field of a details record now, and the
  // records refetch in a background pass, so the old store is dropped rather
  // than migrated.
  if (db.objectStoreNames.contains(AIRSTATUS_STORE)) db.deleteObjectStore(AIRSTATUS_STORE);
}

/** Opens the database, reusing the connection across calls. */
export function openDb(): Promise<IDBDatabase> {
  cached ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.addEventListener("upgradeneeded", () => {
      upgrade(request.result);
    });
    request.addEventListener("success", () => {
      const db = request.result;
      // A newer tab upgrading the schema would otherwise block forever.
      db.addEventListener("versionchange", () => {
        db.close();
        cached = null;
      });
      resolve(db);
    });
    request.addEventListener("error", () => {
      cached = null;
      reject(request.error ?? new Error("Could not open the local database"));
    });
  });
  return cached;
}

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener("success", () => {
      resolve(request.result);
    });
    request.addEventListener("error", () => {
      reject(request.error ?? new Error("Local database request failed"));
    });
  });
}

/** Runs `work` against one store and resolves when the transaction commits. */
export async function withStore<T>(
  storeName: string,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T> | IDBRequest<T>[],
): Promise<T[]> {
  const db = await openDb();
  const tx = db.transaction(storeName, mode);
  const requests = work(tx.objectStore(storeName));
  const all = Array.isArray(requests) ? requests : [requests];
  const results = await Promise.all(all.map((request) => promisify(request)));
  // Await the commit, not just the requests: a readwrite transaction that
  // aborts after its requests succeed would otherwise look like a clean write.
  await new Promise<void>((resolve, reject) => {
    tx.addEventListener("complete", () => {
      resolve();
    });
    tx.addEventListener("error", () => {
      reject(tx.error ?? new Error("Transaction failed"));
    });
    tx.addEventListener("abort", () => {
      reject(tx.error ?? new Error("Transaction aborted"));
    });
  });
  return results;
}

/** Erases everything. Used by sign-out. */
export async function wipe(): Promise<void> {
  const db = await openDb();
  db.close();
  cached = null;
  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.addEventListener("success", () => {
      resolve();
    });
    request.addEventListener("error", () => {
      reject(request.error ?? new Error("Could not clear local data"));
    });
    // Another open tab holds the database; the delete completes when it closes.
    request.addEventListener("blocked", () => {
      resolve();
    });
  });
}
